import { BatteryLow, ExternalLink, Footprints, LocateFixed, MapPin, Phone, Route, UserPlus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDocDate } from '@/utils/locale';
import { formatDistance, nearest, type FleetStatus } from '@/utils/fleetStatus';
import { isMovable, windowLabel, type ScheduleJob } from '@/components/schedule/scheduleModel';
import { initials } from './googleMaps';
import type { MapTech } from './FleetMap';
import { cn } from '@/lib/utils';

const TONE_BADGE: Record<FleetStatus['tone'], 'success' | 'warning' | 'info' | 'secondary'> = {
  success: 'success',
  warning: 'warning',
  info: 'info',
  muted: 'secondary',
};

const LOW_BATTERY = 20;

function positionOf(entry: MapTech) {
  return entry.status.position ? { lat: entry.status.position.lat, lng: entry.status.position.lng } : null;
}

function TechAvatar({ name, colour }: { name: string; colour: string }) {
  return (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
      style={{ background: colour }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

function EtaLine({ status, timezone }: { status: FleetStatus; timezone: string }) {
  if (!status.eta) return null;
  return (
    <span className={cn('text-xs', status.eta.late ? 'text-warning' : 'text-muted-foreground')}>
      {status.eta.late ? 'Late · ' : ''}ETA {formatDocDate(status.eta.expectedAt.toISOString(), 'h:mm a', timezone)}
    </span>
  );
}

// ── Technicians tab ──────────────────────────────────────────────────────────

export function TechList({
  entries, selectedId, timezone, onSelect,
}: {
  entries: MapTech[];
  selectedId: string | null;
  timezone: string;
  onSelect: (techId: string) => void;
}) {
  if (entries.length === 0) {
    return <p className="px-1 py-6 text-center text-sm text-muted-foreground">No active technicians.</p>;
  }
  return (
    <ul className="divide-y">
      {entries.map(({ tech, status, colour }) => (
        <li key={tech.tech_id}>
          <button
            type="button"
            onClick={() => onSelect(tech.tech_id)}
            className={cn(
              'flex w-full items-start gap-3 px-2 py-2.5 text-left hover:bg-muted/50',
              selectedId === tech.tech_id && 'bg-muted',
            )}
          >
            <TechAvatar name={tech.name} colour={colour} />
            <span className="min-w-0 flex-1 space-y-0.5">
              <span className="flex items-center gap-2">
                <span className="truncate font-medium">{tech.name}</span>
                {tech.battery_pct != null && tech.battery_pct < LOW_BATTERY && (
                  <span className="flex items-center gap-0.5 text-xs text-warning" title="Phone battery low">
                    <BatteryLow className="h-3.5 w-3.5" />{tech.battery_pct}%
                  </span>
                )}
              </span>
              <span className="block">
                <Badge variant={TONE_BADGE[status.tone]} className="font-normal">{status.activityLabel}</Badge>
              </span>
              <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{status.locationLabel}</span>
                <EtaLine status={status} timezone={timezone} />
              </span>
            </span>
            {!status.position && <MapPin className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40" aria-label="Not on the map" />}
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Unassigned tab ───────────────────────────────────────────────────────────

export function UnassignedList({
  jobs, entries, unit, onSelectJob, onAssign,
}: {
  jobs: ScheduleJob[];
  entries: MapTech[];
  unit: 'km' | 'mi';
  onSelectJob: (jobId: string) => void;
  onAssign: (job: ScheduleJob, techId: string) => void;
}) {
  if (jobs.length === 0) {
    return <p className="px-1 py-6 text-center text-sm text-muted-foreground">Every job on this day has a technician.</p>;
  }
  return (
    <ul className="divide-y">
      {jobs.map((job) => {
        const near = job.lat != null && job.lng != null
          ? nearest({ lat: Number(job.lat), lng: Number(job.lng) }, entries, positionOf, 3)
          : [];
        return (
          <li key={job.id} className="space-y-1.5 px-2 py-2.5">
            <button type="button" className="block w-full text-left" onClick={() => onSelectJob(job.id)}>
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium">{job.job_number ?? 'Job'}</span>
                <span className="text-xs text-muted-foreground">{windowLabel(job)}</span>
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {[job.customer_name, job.customer_suburb].filter(Boolean).join(' · ') || 'No customer details'}
              </span>
            </button>
            {job.lat == null || job.lng == null ? (
              <p className="text-xs text-muted-foreground">No location for this address yet.</p>
            ) : near.length === 0 ? (
              <p className="text-xs text-muted-foreground">No technician positions to compare.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {near.map(({ item, km }) => (
                  <Button
                    key={item.tech.tech_id}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 px-2 text-xs"
                    disabled={!isMovable(job)}
                    onClick={() => onAssign(job, item.tech.tech_id)}
                    title={`Assign to ${item.tech.name}`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: item.colour }} />
                    {item.tech.name.split(' ')[0]} · {formatDistance(km, unit)}
                  </Button>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Selected job ─────────────────────────────────────────────────────────────

export function JobCard({
  job, entries, techName, unit, onOpen, onAssign, onClose,
}: {
  job: ScheduleJob;
  entries: MapTech[];
  techName: (id: string | null) => string;
  unit: 'km' | 'mi';
  onOpen: () => void;
  onAssign: (techId: string) => void;
  onClose: () => void;
}) {
  const hasCoords = job.lat != null && job.lng != null;
  const near = hasCoords
    ? nearest({ lat: Number(job.lat), lng: Number(job.lng) }, entries.filter((e) => e.tech.tech_id !== job.assigned_tech_id), positionOf, 5)
    : [];
  const movable = isMovable(job);

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{job.job_number ?? 'Job'}</p>
          <p className="truncate text-sm">{job.customer_name || 'Unknown customer'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[job.customer_address, job.customer_suburb].filter(Boolean).join(', ') || 'No address'}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        <span className="text-muted-foreground">{windowLabel(job)}</span>
        <span>{job.assigned_tech_id ? techName(job.assigned_tech_id) : <span className="text-warning">Unassigned</span>}</span>
        {job.service_types?.name && <span className="text-muted-foreground">{job.service_types.name}</span>}
      </div>
      <Button type="button" variant="outline" size="sm" className="w-full" onClick={onOpen}>
        <ExternalLink className="mr-2 h-4 w-4" />
        Open job
      </Button>
      {movable && hasCoords && near.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {job.assigned_tech_id ? 'Reassign to someone closer' : 'Closest technicians'}
          </p>
          <ul className="space-y-1">
            {near.map(({ item, km }) => (
              <li key={item.tech.tech_id} className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.colour }} />
                <span className="min-w-0 flex-1 truncate">
                  {item.tech.name}
                  <span className="text-muted-foreground"> · {formatDistance(km, unit)} · {item.status.activityLabel}</span>
                </span>
                <Button type="button" size="sm" variant="secondary" className="h-7 px-2" onClick={() => onAssign(item.tech.tech_id)}>
                  <UserPlus className="mr-1 h-3.5 w-3.5" />
                  Assign
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">Straight-line distance from each technician’s last position.</p>
        </div>
      )}
      {!movable && <p className="text-xs text-muted-foreground">This job can’t be moved: it has started or is closed.</p>}
    </div>
  );
}

// ── Selected technician ──────────────────────────────────────────────────────

export function TechCard({
  entry, jobs, timezone, canSeeTrail, showTrail, trailPoints, following,
  onToggleTrail, onToggleFollow, onOpenJob, onClose,
}: {
  entry: MapTech;
  jobs: ScheduleJob[];
  timezone: string;
  canSeeTrail: boolean;
  showTrail: boolean;
  trailPoints: number | null;
  following: boolean;
  onToggleTrail: () => void;
  onToggleFollow: () => void;
  onOpenJob: (jobId: string) => void;
  onClose: () => void;
}) {
  const { tech, status, colour } = entry;
  const ordered = [...jobs].sort((a, b) => (a.scheduled_time_start ?? '99').localeCompare(b.scheduled_time_start ?? '99'));

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <div className="flex items-start gap-3">
        <TechAvatar name={tech.name} colour={colour} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{tech.name}</p>
          <Badge variant={TONE_BADGE[status.tone]} className="mt-0.5 font-normal">{status.activityLabel}</Badge>
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-1 text-sm">
        <p className="text-muted-foreground">
          {status.locationLabel}
          {status.position?.source === 'job' ? ' · shown at the job they started' : ''}
          {status.position?.accuracyM ? ` · within ~${Math.round(status.position.accuracyM)} m` : ''}
        </p>
        <EtaLine status={status} timezone={timezone} />
        {tech.battery_pct != null && (
          <p className={cn('text-xs', tech.battery_pct < LOW_BATTERY ? 'text-warning' : 'text-muted-foreground')}>
            Phone battery {tech.battery_pct}%
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {tech.phone && (
          <Button asChild type="button" size="sm" variant="outline">
            <a href={`tel:${tech.phone}`}><Phone className="mr-2 h-4 w-4" />Call</a>
          </Button>
        )}
        {status.position && (
          <Button type="button" size="sm" variant={following ? 'secondary' : 'outline'} onClick={onToggleFollow} aria-pressed={following}>
            <LocateFixed className="mr-2 h-4 w-4" />
            {following ? 'Following' : 'Follow'}
          </Button>
        )}
        {canSeeTrail && (
          <Button type="button" size="sm" variant={showTrail ? 'secondary' : 'outline'} onClick={onToggleTrail} aria-pressed={showTrail}>
            <Footprints className="mr-2 h-4 w-4" />
            {showTrail ? 'Hide trail' : 'Show trail'}
          </Button>
        )}
      </div>
      {canSeeTrail && showTrail && trailPoints != null && (
        <p className="text-xs text-muted-foreground">
          {trailPoints === 0 ? 'No recorded positions on this day.' : `${trailPoints} position${trailPoints === 1 ? '' : 's'} recorded. Viewing a trail is logged.`}
        </p>
      )}

      <div className="space-y-1">
        <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Route className="h-3.5 w-3.5" />
          Jobs on this day
        </p>
        {ordered.length === 0 ? (
          <p className="text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="space-y-0.5">
            {ordered.map((job) => (
              <li key={job.id}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-sm hover:bg-muted/50',
                    status.activeJob?.id === job.id && 'bg-info/10',
                  )}
                  onClick={() => onOpenJob(job.id)}
                >
                  <span className={cn('truncate', job.status === 'completed' && 'text-muted-foreground line-through')}>
                    {job.job_number ?? 'Job'} · {job.customer_suburb ?? job.customer_name ?? ''}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">{windowLabel(job)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
